import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { apiClient } from '../api/client';

export function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // BETA features state
  const [betaModeEnabled, setBetaModeEnabled] = useState(false);
  const [loadingBeta, setLoadingBeta] = useState(true);
  const [updatingBeta, setUpdatingBeta] = useState(false);

  // Algorithm visibility state
  const [algorithmSettings, setAlgorithmSettings] = useState({
    show_algorithm_v1: true,
    show_algorithm_v2: true,
    show_algorithm_v3: true,
    show_algorithm_v4: false,
    show_algorithm_v5: true,
    show_algorithm_v6: true,
    show_algorithm_super: true,
    show_algorithm_super_beta: false,
    show_algorithm_draft: true,
  });
  const [loadingAlgorithms, setLoadingAlgorithms] = useState(true);
  const [updatingAlgorithms, setUpdatingAlgorithms] = useState(false);

  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Account deletion state
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const loadBetaMode = async () => {
      try {
        const response = await apiClient.getBetaModeEnabled();
        setBetaModeEnabled(response.beta_mode_enabled);
      } catch (err) {
        console.error('Failed to load BETA mode status:', err);
      } finally {
        setLoadingBeta(false);
      }
    };

    const loadAlgorithmSettings = async () => {
      try {
        const response = await apiClient.getAlgorithmVisibility();
        setAlgorithmSettings(response.data);
      } catch (err) {
        console.error('Failed to load algorithm visibility:', err);
      } finally {
        setLoadingAlgorithms(false);
      }
    };

    loadBetaMode();
    loadAlgorithmSettings();
  }, []);

  const handleToggleBetaMode = async () => {
    setError('');
    setSuccess('');

    try {
      setUpdatingBeta(true);
      const newValue = !betaModeEnabled;
      await apiClient.updateBetaModeEnabled(newValue);
      setBetaModeEnabled(newValue);
      setSuccess(newValue ? 'BETA features enabled!' : 'BETA features disabled');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update BETA mode');
    } finally {
      setUpdatingBeta(false);
    }
  };

  const handleToggleAlgorithm = async (key: string) => {
    setError('');
    setSuccess('');

    try {
      setUpdatingAlgorithms(true);
      const newSettings = {
        ...algorithmSettings,
        [key]: !algorithmSettings[key as keyof typeof algorithmSettings],
      };
      await apiClient.updateAlgorithmVisibility(newSettings);
      setAlgorithmSettings(newSettings);
      setSuccess('Algorithm visibility updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update algorithm visibility');
    } finally {
      setUpdatingAlgorithms(false);
    }
  };

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newEmail || !emailPassword) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setChangingEmail(true);
      const response = await apiClient.changeEmail(newEmail, emailPassword);
      setSuccess(response.message || 'Email changed successfully!');
      setNewEmail('');
      setEmailPassword('');
      // Reload user data
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change email');
    } finally {
      setChangingEmail(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setChangingPassword(true);
      const response = await apiClient.changePassword(currentPassword, newPassword);
      setSuccess(response.message || 'Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!deletePassword || deleteConfirm !== 'DELETE') {
      setError('Please enter your password and type DELETE to confirm');
      return;
    }

    try {
      setDeleting(true);
      await apiClient.deleteAccount(deletePassword);
      await logout();
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-2xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage your account settings and preferences
          </p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Account Info */}
        <Card>
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Account Information</h2>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Name</div>
              <div className="font-medium text-gray-900 dark:text-gray-100">{user?.name || 'Not set'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Email</div>
              <div className="font-medium text-gray-900 dark:text-gray-100">{user?.email}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Email Verified</div>
              <div className="font-medium">
                {user?.email_verified ? (
                  <span className="text-green-600 dark:text-green-400">✓ Verified</span>
                ) : (
                  <span className="text-yellow-600 dark:text-yellow-400">Not verified</span>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* BETA Features */}
        <Card className="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">BETA Features</h2>
            <span className="px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-200 rounded">
              BETA
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Try experimental features before they're officially released. You can opt-in or opt-out at any time.
          </p>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg">
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-gray-100">Position Slider System</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Use a continuous 0-100 position preference slider instead of discrete FWD/MID/DEF positions.
                  Enables Model 4 team generation algorithm with weighted position ratings.
                </div>
              </div>
              <button
                onClick={handleToggleBetaMode}
                disabled={loadingBeta || updatingBeta}
                className={`
                  ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${betaModeEnabled ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-700'}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                    transition duration-200 ease-in-out
                    ${betaModeEnabled ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>

            {betaModeEnabled && (
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <div className="text-sm text-purple-800 dark:text-purple-300">
                  <div className="font-semibold mb-1">BETA Features Enabled</div>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Position slider available in Players page (BETA Positions tab)</li>
                    <li>Model 4 algorithm available in Generate Teams page</li>
                    <li>Weighted position ratings calculated automatically</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Algorithm Visibility */}
        <Card>
          <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Algorithm Visibility</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Choose which team generation algorithms appear on the Generate Teams page.
          </p>

          <div className="space-y-3">
            {[
              { key: 'show_algorithm_v1', label: 'V1: Rating Balance', description: 'Basic rating-based team balancing' },
              { key: 'show_algorithm_v2', label: 'V2: Chemistry', description: 'Player chemistry and familiarity' },
              { key: 'show_algorithm_v3', label: 'V3: Attribute Balance', description: 'Detailed attribute-level balancing' },
              { key: 'show_algorithm_super', label: 'Super: Multi-dimensional', description: 'Combined position, chemistry, and variety' },
              { key: 'show_algorithm_v5', label: 'V5: Dynamic Equilibrium', description: 'Iterative optimization for perfect balance' },
              { key: 'show_algorithm_v6', label: 'V6: Contribution + Chemistry', description: 'AI-calibrated weights based on match outcomes' },
              { key: 'show_algorithm_draft', label: 'Draft Mode', description: 'Captain-based team selection' },
              { key: 'show_algorithm_v4', label: 'V4: Position Slider (BETA)', description: 'Weighted position preferences', isBeta: true },
              { key: 'show_algorithm_super_beta', label: 'Super BETA', description: 'Experimental super algorithm', isBeta: true },
            ].map(({ key, label, description, isBeta }) => (
              <div key={key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{label}</div>
                    {isBeta && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-200 rounded">
                        BETA
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{description}</div>
                </div>
                <button
                  onClick={() => handleToggleAlgorithm(key)}
                  disabled={loadingAlgorithms || updatingAlgorithms}
                  className={`
                    ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${algorithmSettings[key as keyof typeof algorithmSettings] ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                      transition duration-200 ease-in-out
                      ${algorithmSettings[key as keyof typeof algorithmSettings] ? 'translate-x-5' : 'translate-x-0'}
                    `}
                  />
                </button>
              </div>
            ))}
          </div>
        </Card>

        {/* Change Email */}
        <Card>
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Change Email</h2>
          <form onSubmit={handleChangeEmail} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Email
              </label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="new@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Current Password
              </label>
              <Input
                type="password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>
            <Button type="submit" loading={changingEmail}>
              Change Email
            </Button>
          </form>
        </Card>

        {/* Change Password */}
        <Card>
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Change Password</h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Current Password
              </label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Password
              </label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password (min 8 characters)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm New Password
              </label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
            <Button type="submit" loading={changingPassword}>
              Change Password
            </Button>
          </form>
        </Card>

        {/* Delete Account */}
        <Card className="border-red-200 dark:border-red-800">
          <h2 className="text-lg font-semibold mb-2 text-red-700 dark:text-red-400">Danger Zone</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Once you delete your account, there is no going back. All your data will be permanently deleted.
          </p>
          <form onSubmit={handleDeleteAccount} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <Input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type DELETE to confirm
              </label>
              <Input
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
              />
            </div>
            <Button
              type="submit"
              variant="ghost"
              loading={deleting}
              className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
            >
              Delete Account
            </Button>
          </form>
        </Card>
      </div>
    </Layout>
  );
}
